﻿import { useMsal } from '@azure/msal-react';
import { Button, makeStyles, Tab, TabList } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useStores } from '../../common/hooks';
import { EmailPageRouteStates, OperationTypes } from '../../models/route-states';
import { RoutePaths } from '../../route-paths';
import { parseAsString, useQuery } from '../../shared/hooks';
import { CustomMessageBar, PageTitle } from '../shared-components';
import { FlexBox } from '../shared-components/flex-box';
import Loading from '../shared-components/Loading';
import { MyTemplatesTable, SentEmailsTable } from './components';
import './EmailPage.scss';

type EmailPageTabType = 'sent-emails' | 'my-templates';

const useStyle = makeStyles({
  tabList: { gap: 0, },
  tabButton: { borderRadius: 0, },
});

const EmailPageImpl: React.FC = () => {
  const {
    instance,
  } = useMsal();

  const {
    apiStore,
    awardStore,
    emailPageUiStore,
  } = useStores();

  const {
    urlSearchParams,
    getQueryParam,
    updateQuery,
  } = useQuery();

  const styles = useStyle();

  const [isLoading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();
  const routeStates = location.state as EmailPageRouteStates | undefined;

  const currentTab = getQueryParam<EmailPageTabType>('tab', parseAsString<EmailPageTabType>) || 'sent-emails';

  const setCurrentTab = (tab: EmailPageTabType) => {
    updateQuery(
      { tab },
      { replace: true }
    );
  };

  const applyFilters = () => {
    updateQuery(
      {
        ['tab']: currentTab
      },
      { replace: true }
    );
  };

  const deleteEmailTemplate = async (emailTemplateId: number): Promise<void> => {
    if (!awardStore.defaultProgramRef) return;
    setLoading(true);
    try {
      await apiStore.deleteEmailTemplate(instance, awardStore.defaultProgramRef.programId, emailTemplateId);
      const emailTemplateRefs = await apiStore.getEmailTemplateRefs(instance, awardStore.defaultProgramRef.programId);
      emailPageUiStore.setEmailTemplateRefs(emailTemplateRefs);
    } catch (error) {
      // TODO - handle error
    } finally {
      setLoading(false);
    }
  };

  const onEditEmailTemplate = (emailTemplateId: number): void => {
    navigate(`${RoutePaths.Email}/${emailTemplateId}/edit`);
  };

  const onCreateEmailTemplate = (): void => {
    navigate(`${RoutePaths.Email}/create`);
  };

  const renderToastMessage = (operationType: OperationTypes): string => {
    if (operationType === 'send') return 'sent';
    if (operationType === 'create') return 'created';
    if (operationType === 'update') return 'updated';
    return '';
  };

  const loadData = async () => {
    if (!awardStore.defaultProgramRef) return;
    setLoading(true);
    try {
      if (currentTab === 'sent-emails') {
        const sentEmails = await apiStore.getSentEmails(instance, awardStore.defaultProgramRef.programId);
        emailPageUiStore.setSentEmails(sentEmails);
      } else if (currentTab === 'my-templates') {
        const emailTemplateRefs = await apiStore.getEmailTemplateRefs(instance, awardStore.defaultProgramRef.programId);
        emailPageUiStore.setEmailTemplateRefs(emailTemplateRefs);
      }
    } catch (error) {
      // TODO - handle error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [urlSearchParams]);

  useEffect(() => {
    applyFilters();
    return () => {
      emailPageUiStore.resetStates();
    }
  }, []);

  return (
    <div className='email-page'>
      {isLoading && <Loading />}
      {
        routeStates?.operationType && (
          <CustomMessageBar
            message={`Successfully ${renderToastMessage(routeStates?.operationType)} an email!`}
            notificationTypeCode='success'
            showDismissButton
            onDismissClicked={() => navigate(location.pathname, { replace: true, state: undefined })}
          />
        )
      }
      <PageTitle
        title='Emails'
        subtitle={awardStore.defaultProgramRef ? awardStore.defaultProgramRef.displayName : ''}
      />
      <FlexBox
        content='space-between'
        className='email-page__tab-nav'
      >
        <TabList
          appearance='filled-circular'
          selectedValue={currentTab}
          onTabSelect={(_, tabData) => setCurrentTab(tabData.value as EmailPageTabType)}
          className={styles.tabList}
        >
          <Tab className={styles.tabButton} value='sent-emails'>Sent emails</Tab>
          <Tab className={styles.tabButton} value='my-templates'>My templates</Tab>
        </TabList>
        <Button
          appearance='primary'
          onClick={() => onCreateEmailTemplate()}
        >
          + Add new
        </Button>
      </FlexBox>
      <div className='email-page__tab-content'>
        {
          currentTab === 'sent-emails' &&
          <SentEmailsTable />
        }
        {
          currentTab === 'my-templates' &&
          <MyTemplatesTable
            onEditEmailTemplate={(emailTemplateId) => onEditEmailTemplate(emailTemplateId)}
            onDeleteEmailTemplate={(emailTemplateId) => deleteEmailTemplate(emailTemplateId)}
          />
        }
      </div>
    </div>
  );
}

export const EmailPage = observer(EmailPageImpl);
